import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Minus, Plus, Trash2, Truck, X } from "lucide-react";
import { useStore } from "../../context/StoreContext";
import { fmtTRY } from "../../lib/format";

export default function CartDrawer() {
  const { cart, cartOpen, setCartOpen, updateItem, removeItem } = useStore();
  const navigate = useNavigate();
  const lines = cart?.lines || [];
  const threshold = cart?.free_shipping_threshold || 0;
  const missing = cart?.amount_to_free_shipping || 0;
  const progress = threshold > 0 ? Math.min(100, ((threshold - missing) / threshold) * 100) : 100;

  return (
    <AnimatePresence>
      {cartOpen && (
        <div className="fixed inset-0 z-50" data-testid="cart-drawer">
          <motion.div className="absolute inset-0 bg-black/45 backdrop-blur-[2px]"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setCartOpen(false)} />
          <motion.aside
            className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-[#FAF9F5] flex flex-col shadow-2xl"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
          >
            <div className="flex items-center justify-between p-5 border-b border-[#E6E4DD]">
              <h2 className="font-display font-extrabold text-xl">Sepetim <span className="text-neutral-400 font-medium text-sm">({cart?.item_count || 0})</span></h2>
              <button onClick={() => setCartOpen(false)} className="p-2 hover:bg-[#F4F2EB] rounded-full" data-testid="cart-drawer-close" aria-label="Kapat"><X size={20} /></button>
            </div>

            {threshold > 0 && lines.length > 0 && (
              <div className="px-5 py-3 bg-white border-b border-[#E6E4DD]">
                {missing > 0 ? (
                  <p className="text-xs text-neutral-600 mb-1.5 flex items-center gap-1.5">
                    <Truck size={14} className="text-amber-600" />
                    <span><b>{fmtTRY(missing)}</b> daha ekleyin, kargo bedava!</span>
                  </p>
                ) : (
                  <p className="text-xs text-emerald-700 font-semibold mb-1.5 flex items-center gap-1.5"><Truck size={14} /> Kargonuz ücretsiz!</p>
                )}
                <div className="h-1.5 bg-[#F4F2EB] rounded-full overflow-hidden">
                  <motion.div className="h-full bg-gradient-to-r from-amber-500 to-emerald-500 rounded-full"
                    initial={{ width: 0 }} animate={{ width: `${progress}%` }} transition={{ duration: 0.6 }} />
                </div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {lines.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center gap-3 text-neutral-400">
                  <ShoppingBagX />
                  <p className="text-sm">Sepetiniz henüz boş</p>
                  <button onClick={() => { setCartOpen(false); navigate("/ara"); }} className="text-amber-700 font-semibold text-sm underline" data-testid="cart-empty-browse">Alışverişe başla</button>
                </div>
              ) : lines.map((line) => (
                <div key={`${line.product_id}-${line.variant_id || "base"}`} className="flex gap-3 bg-white rounded-xl border border-[#E6E4DD] p-3" data-testid="cart-drawer-item">
                  <Link to={`/urun/${line.slug}`} onClick={() => setCartOpen(false)} className="w-20 h-24 rounded-lg overflow-hidden bg-[#F4F2EB] shrink-0">
                    {line.image && <img src={line.image} alt={line.name} className="w-full h-full object-cover" />}
                  </Link>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm leading-snug line-clamp-1">{line.name}</p>
                    {line.variant_label && <p className="text-[11px] text-neutral-500 mt-0.5">{line.variant_label}</p>}
                    {line.customization && (
                      <p className="text-[11px] text-amber-700 mt-0.5 flex items-center gap-1.5" data-testid="cart-customization">
                        {line.customization.photo_url && <img src={line.customization.photo_url} alt="Kişiselleştirme" className="w-6 h-6 rounded object-cover border border-[#E6E4DD]" />}
                        {line.customization.text ? `"${line.customization.text}"` : "Fotoğraf eklendi"}
                      </p>
                    )}
                    <p className="font-mono-ed text-[10px] text-neutral-400 mt-0.5">{line.sku}</p>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center border border-[#E6E4DD] rounded-full">
                        <button className="p-1.5 hover:text-amber-700 disabled:opacity-30" disabled={line.quantity <= 1}
                          onClick={() => updateItem(line.product_id, line.variant_id, line.quantity - 1)} data-testid="cart-item-minus"><Minus size={13} /></button>
                        <span className="w-7 text-center text-sm font-semibold" data-testid="cart-item-qty">{line.quantity}</span>
                        <button className="p-1.5 hover:text-amber-700 disabled:opacity-30" disabled={line.quantity >= line.stock}
                          onClick={() => updateItem(line.product_id, line.variant_id, line.quantity + 1)} data-testid="cart-item-plus"><Plus size={13} /></button>
                      </div>
                      <div className="text-right">
                        <p className="font-mono-ed font-bold text-sm">{fmtTRY(line.line_total)}</p>
                        {line.final_unit_price < line.unit_price && (
                          <p className="text-[10px] text-neutral-400 line-through">{fmtTRY(line.unit_price * line.quantity)}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  <button onClick={() => removeItem(line.product_id, line.variant_id)} className="self-start p-1 text-neutral-300 hover:text-rose-600 transition-colors" data-testid="cart-item-remove" aria-label="Kaldır">
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
              {cart?.issues?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 space-y-1" data-testid="cart-issues">
                  {cart.issues.map((i, idx) => <p key={idx}>⚠ {i.issue}</p>)}
                </div>
              )}
            </div>

            {lines.length > 0 && (
              <div className="p-5 border-t border-[#E6E4DD] bg-white space-y-2 text-sm">
                <div className="flex justify-between text-neutral-500"><span>Ara toplam</span><span>{fmtTRY(cart?.subtotal)}</span></div>
                {cart?.campaign_discount > 0 && <div className="flex justify-between text-emerald-700"><span>Kampanya indirimi</span><span>-{fmtTRY(cart.campaign_discount)}</span></div>}
                {cart?.coupon_discount > 0 && <div className="flex justify-between text-emerald-700"><span>Kupon ({cart.coupon_code})</span><span>-{fmtTRY(cart.coupon_discount)}</span></div>}
                <div className="flex justify-between text-neutral-500"><span>Kargo</span><span>{cart?.shipping_fee === 0 ? "Ücretsiz" : fmtTRY(cart?.shipping_fee)}</span></div>
                <div className="flex justify-between font-mono-ed font-bold text-lg pt-2 border-t border-[#E6E4DD]"><span>Toplam</span><span data-testid="cart-drawer-total">{fmtTRY(cart?.total)}</span></div>
                <button
                  onClick={() => { setCartOpen(false); navigate("/odeme"); }}
                  className="w-full h-12 mt-2 rounded-full bg-[#121214] text-white font-semibold hover:bg-amber-600 transition-colors"
                  data-testid="cart-checkout-button"
                >
                  Ödemeye Geç
                </button>
                <button onClick={() => { setCartOpen(false); navigate("/sepet"); }} className="w-full h-10 rounded-full border border-[#E6E4DD] text-sm hover:bg-[#F4F2EB] transition-colors" data-testid="cart-view-button">
                  Sepeti Görüntüle
                </button>
              </div>
            )}
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );
}

function ShoppingBagX() {
  return (
    <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
      <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" /><path d="M3 6h18" /><path d="M16 10a4 4 0 0 1-8 0" />
    </svg>
  );
}
